import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch, AppState } from 'app/store'
import { requestAuth, requestCleverAuth } from 'services/AuthService'
import { LoginForm } from 'services/modals/loginForm'
import { setAuthCoookies } from 'util/cookies'
import { setError } from './errorSlice'
import { setLoading, clearLoading, clearLoadingClever } from './pageSlice'
import cookie from 'js-cookie'
import { resetAssessmentReducer } from './assessmentSlice'
import { resetStudentReducer } from './studentSlice'
import { studentApi } from 'services/studentApi'
import { ContrastMode, DEFAULT_CONTRAST_MODE } from 'config/theme'
import { setThemeContrast } from './themeSlice'

export interface User {
  first_name: string
  last_name: string
  student_id: string
  last_login: string
  token: string
  school_id: string
  school_name: string
  id_student: number
  contrast_preset: Uppercase<ContrastMode>
  avatar_background_color?: string
  avatar_text_color?: string
  available_color_codes?: { id: number; code: string }[]
  selected_color?: {
    color_code_id?: number
    fk_color_code_id?: number
  }
}

interface AuthState {
  user: User | null
}
// test-deploy
const initialState: AuthState = {
  user: null
}

export const authSlice = createSlice({
  name: 'authReducer',
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload.user
    },
    setUserAvatarColors: (
      state,
      action: PayloadAction<
        Pick<User, 'avatar_background_color' | 'avatar_text_color'>
      >
    ) => {
      /**
       * Keep the API-resolved avatar colors on the authenticated Redux user
       * so all headers and profile surfaces share the same current values.
       */
      if (!state.user) {
        return
      }

      state.user.avatar_background_color =
        action.payload.avatar_background_color
      state.user.avatar_text_color = action.payload.avatar_text_color
    }
  }
})

export default authSlice.reducer

export const { setUser, setUserAvatarColors } = authSlice.actions

export const selectUser = (state: AppState) => state.authReducer.user

export const loginAction =
  (requestData: LoginForm, navigate: any) => async (dispatch: AppDispatch) => {
    dispatch(setLoading())
    try {
      const user = await requestAuth(requestData)
      if (window.location.hostname === 'localhost') {
        setAuthCoookies(user)
      } else {
        setAuthCoookies(user, true)
      }
      dispatch(
        setUser({
          user: {
            ...user
          }
        })
      )
      const userContrast: ContrastMode =
        user.contrast_preset?.toLowerCase() || DEFAULT_CONTRAST_MODE
      dispatch(setThemeContrast(userContrast))
      navigate('/classroom')
    } catch (error) {
      dispatch(setError('Invalid Last Name or Student ID'))
    } finally {
      dispatch(clearLoading())
    }
  }

export const loginCleverAction =
  (params: any, navigate: any) => async (dispatch: AppDispatch) => {
    try {
      const result = await requestCleverAuth(params)
      if (result && result.data) {
        if (window.location.hostname === 'localhost') {
          setAuthCoookies(result.data)
        } else {
          setAuthCoookies(result.data, true)
        }
        dispatch(
          setUser({
            user: result.data
          })
        )
        navigate('/classroom')
      }
    } catch (error: any) {
      console.error(
        error,
        ' $$ Error ',
        error?.response,
        ' @@ response ',
        error?.response?.data,
        ' !! data'
      )
      if (error?.response?.data) {
        dispatch(setError(error?.response?.data?.message))
      } else {
        if (params.type === 'classlink') {
          dispatch(setError('Failed to Login with Classlink'))
        } else {
          dispatch(setError('Failed to Login with Clever'))
        }
      }
    } finally {
      dispatch(dispatch(clearLoadingClever()))
      dispatch(clearLoading())
    }
  }

export const logout = () => async (dispatch: AppDispatch) => {
  cookie.remove('email')
  cookie.remove('token')
  cookie.remove('first_name')
  cookie.remove('last_name')
  cookie.remove('classroom_id')
  cookie.remove('class_name')
  cookie.remove('isMath')
  cookie.remove('text_to_speech_enabled')
  cookie.remove('assessmentName')
  cookie.remove('allow_ti84_calculator')
  cookie.remove('allow_calculator')

  dispatch(
    setUser({
      user: null!
    })
  )
  dispatch(resetAssessmentReducer())
  dispatch(resetStudentReducer())
  dispatch(studentApi.util.resetApiState())
}
